import { useCallback, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useBlocker } from 'react-router'
import classnames from 'classnames'
import { FieldDropdown, IconButton, SpinnerLoader, Typography } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { AdditionalBreakpointsEnum } from 'shared/constants/breakpoints'
import { MessagePreview } from 'widgets/MessagePreview'
import { SMSMessageField } from 'widgets/MessageFormFields'
import { TagsDropdown } from 'widgets/TagsDropdown'
import { SendFrom } from 'widgets/SendFrom'
import { MobilePreview } from 'widgets/MobilePreview'
import { ClickToTextName } from 'pages/clickToText/pages/createClickToText/ui/ClickToTextFields'
import { useClickToTextViewContext } from 'pages/clickToText/pages/viewClickToText/context'
import { useClickToTextPageContext } from 'pages/clickToText/context'
import { ClickToTextActions } from './ClickToTextActions'
import styles from './styles.module.scss'

export const ClickToTextViewContent = observer(() => {
  const {
    clickToTextSMSStore: { smsMessageFieldStore, messageFieldStore },
    errorsMap,
    setDraggingCondition,
    isDraggingCondition,
    tagsControlStore,
    handleSaveClickToText,
    isSaving,
    isExistChanges,
    handleShowKeepEditingModal,
    reset,
    isLoading,
    isEditMode,
    sendFromStore,
    removeError,
  } = useClickToTextViewContext()
  const { pageLayoutStore } = useClickToTextPageContext()
  const {
    titleIconProps,
    handleToggleCollapse,
    headerMenuTooltipIconProps,
    setCustomCollapseWidth,
  } = pageLayoutStore
  const smsMessageError = errorsMap.get('message')
  const blocker = useBlocker(() => isExistChanges)

  const beforeUnloadHandler = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault()
  }, [])

  useEffect(() => {
    return () => {
      reset()
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [])

  useEffect(() => {
    uiStore.setRoutesBlocker(blocker)
    handleShowKeepEditingModal(blocker)
  }, [blocker])

  useEffect(() => {
    uiStore.setRoutesBlocker(isExistChanges ? blocker : null)

    if (isExistChanges) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [isExistChanges])

  useEffect(() => {
    setCustomCollapseWidth(AdditionalBreakpointsEnum['1280'])
    return () => {
      setCustomCollapseWidth(null)
    }
  }, [setCustomCollapseWidth])

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <SpinnerLoader ariaLabel={'ClickToTextViewContent'} />
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.wrapCollapseBtn}>
        <IconButton
          ariaLabel={'collapseBtn'}
          iconProps={titleIconProps}
          variant={'icon'}
          color={'secondary'}
          onClick={handleToggleCollapse}
          tooltipProps={headerMenuTooltipIconProps}
        />
      </div>
      <div className={styles.wrapMobilePreviewBtn}>
        <MobilePreview>
          <MessagePreview type={'sms'} messageStore={messageFieldStore} />
        </MobilePreview>
      </div>
      <div className={styles.left}>
        <div
          className={classnames(styles.viewBox, {
            [styles.viewBox__isRelative]: isDraggingCondition,
          })}
        >
          <Typography
            variant={'body-xs-regular'}
            className={styles.label}
            ariaLabel={isEditMode ? 'EditLink' : 'NewLink'}
          >
            {isEditMode ? 'Edit link' : 'New link'}
          </Typography>
          <ClickToTextName />
          <SMSMessageField
            smsMessageFieldStore={smsMessageFieldStore}
            errorMessage={smsMessageError}
            setDraggingCondition={setDraggingCondition}
          />
          {/* TODO: add support for RCS: unknown ticket */}
          {/* <RcsEmptyFallbackInboxAlert alertProps={{ marginTop: 32 }} /> */}
          <div className={styles.section}>
            <div className={styles.section__box}>
              <FieldDropdown
                labelText={'Text in number'}
                dropdownComponent={
                  <SendFrom
                    store={sendFromStore}
                    triggerVariant={'textField'}
                    textFiledTriggerProps={{
                      textFieldPlaceholderProps: {
                        size: 'small',
                        variant: 'outline',
                      },
                    }}
                    placeholder={'Select'}
                    onClearError={() => removeError('numbers')}
                  />
                }
              />
            </div>
            <div className={styles.section__box}>
              <TagsDropdown controlStore={tagsControlStore} labelText='Tags' />
            </div>
          </div>
          <ClickToTextActions
            handleSave={handleSaveClickToText}
            isSaving={isSaving}
            isEditMode={isEditMode}
          />
        </div>
      </div>

      <div className={styles.right}>
        <MessagePreview type={'sms'} messageStore={messageFieldStore} />
      </div>
    </div>
  )
})
